import React, { useState, useEffect } from 'react';

const OrganizerDashboard = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');
  const [stats, setStats] = useState({});

  useEffect(() => {
    loadOrganizerEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      loadEventRegistrations(selectedEvent.id);
    }
  }, [selectedEvent]);

  const loadOrganizerEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/events?organizer=true', {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();
      if (data.success) {
        setEvents(data.events);
        if (data.events.length > 0) {
          setSelectedEvent(data.events[0]);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des événements:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEventRegistrations = async (eventId) => {
    try {
      const response = await fetch(`/api/v1/events/${eventId}/registrations`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();
      if (data.success) {
        setRegistrations(data.registrations);
        setStats(data.statistics);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des inscriptions:', error);
    }
  };

  const handleCheckIn = async (registrationId) => {
    try {
      const response = await fetch(`/api/v1/events/${selectedEvent.id}/check_in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ registration_id: registrationId })
      });

      const data = await response.json();
      if (data.success) {
        // Recharger les inscriptions
        loadEventRegistrations(selectedEvent.id);
        alert(`✅ ${data.message}`);
      } else {
        alert(`❌ ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur lors du check-in:', error);
      alert('❌ Erreur lors du check-in');
    }
  };

  const promoteFromWaitlist = async (registrationId) => {
    try {
      const response = await fetch(`/api/v1/events/${selectedEvent.id}/promote_waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ registration_id: registrationId })
      });

      const data = await response.json();
      if (data.success) {
        loadEventRegistrations(selectedEvent.id);
        alert(`✅ ${data.message}`);
      } else {
        alert(`❌ ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur lors de la promotion:', error);
      alert('❌ Erreur lors de la promotion');
    }
  };

  const exportRegistrations = () => {
    const csv = [
      ['Nom', 'Email', 'Statut', 'Montant payé', 'Date inscription', 'Check-in'],
      ...registrations.map(reg => [
        reg.user.name,
        reg.user.email,
        reg.status,
        `${reg.payment_amount}€`,
        new Date(reg.created_at).toLocaleString('fr-FR'),
        reg.checked_in ? 'Oui' : 'Non'
      ])
    ].map(row => row.join(';')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `inscriptions_${selectedEvent.title.replace(/[^a-z0-9]/gi, '_')}.csv`;
    link.click();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      registered: { class: 'success', icon: '✅', text: 'Inscrit' },
      waitlist: { class: 'warning', icon: '⏳', text: 'Liste d\'attente' },
      cancelled: { class: 'danger', icon: '❌', text: 'Annulé' },
      attended: { class: 'info', icon: '🎉', text: 'Présent' }
    };
    
    const s = statusMap[status] || statusMap.registered;
    return <span className={`badge bg-${s.class}`}>{s.icon} {s.text}</span>;
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="organizer-dashboard">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>🎯 Tableau de bord organisateur</h2>
          <a href="/events/new" className="btn btn-primary">
            ➕ Nouvel événement
          </a>
        </div>

      <div className="row">
        {/* Sidebar avec liste des événements */}
        <div className="col-md-4 col-lg-3">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">Mes événements</h6>
            </div>
            <div className="list-group list-group-flush">
              {events.map(event => (
                <button
                  key={event.id}
                  type="button"
                  className={`list-group-item list-group-item-action ${selectedEvent?.id === event.id ? 'active' : ''}`}
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{event.title}</h6>
                      <small className="text-muted">{formatDate(event.start_date)}</small>
                      <br />
                      <small>
                        <span className={`badge bg-${event.status === 'published' ? 'success' : event.status === 'draft' ? 'secondary' : 'primary'} me-1`}>
                          {event.status}
                        </span>
                        {event.registered_count}/{event.capacity}
                      </small>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="col-md-8 col-lg-9">
          {selectedEvent ? (
            <>
              {/* En-tête de l'événement sélectionné */}
              <div className="card mb-4">
                <div className="card-header">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h5 className="mb-1">{selectedEvent.title}</h5>
                      <div className="text-muted">
                        📅 {formatDate(selectedEvent.start_date)} • 📍 {selectedEvent.location}
                      </div>
                    </div>
                    <div className="btn-group">
                      <a
                        href={`/events/${selectedEvent.id}`}
                        className="btn btn-sm btn-outline-primary"
                        target="_blank"
                      >
                        👁️ Voir
                      </a>
                      <a
                        href={`/events/${selectedEvent.id}/edit`}
                        className="btn btn-sm btn-outline-secondary"
                      >
                        ✏️ Modifier
                      </a>
                    </div>
                  </div>
                </div>
                
                {/* Statistiques rapides */}
                <div className="card-body">
                  <div className="row text-center">
                    <div className="col-3">
                      <div className="h4 text-primary mb-0">{stats.registered_count || 0}</div>
                      <small className="text-muted">Inscrits</small>
                    </div>
                    <div className="col-3">
                      <div className="h4 text-warning mb-0">{stats.waitlist_count || 0}</div>
                      <small className="text-muted">Liste d'attente</small>
                    </div>
                    <div className="col-3">
                      <div className="h4 text-success mb-0">{stats.checked_in_count || 0}</div>
                      <small className="text-muted">Présents</small>
                    </div>
                    <div className="col-3">
                      <div className="h4 text-info mb-0">{stats.total_revenue || 0}€</div>
                      <small className="text-muted">Revenus</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Onglets */}
              <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'registrations' ? 'active' : ''}`}
                    onClick={() => setActiveTab('registrations')}
                  >
                    👥 Inscriptions ({registrations.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'checkin' ? 'active' : ''}`}
                    onClick={() => setActiveTab('checkin')}
                  >
                    ✅ Check-in
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'statistics' ? 'active' : ''}`}
                    onClick={() => setActiveTab('statistics')}
                  >
                    📊 Statistiques
                  </button>
                </li>
              </ul>

              {/* Contenu des onglets */}
              {activeTab === 'registrations' && (
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Liste des inscriptions</h6>
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={exportRegistrations}
                      disabled={registrations.length === 0}
                    >
                      📥 Exporter CSV
                    </button>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Participant</th>
                          <th>Statut</th>
                          <th>Montant</th>
                          <th>Inscription</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registrations.map(registration => (
                          <tr key={registration.id}>
                            <td>
                              <div>
                                <strong>{registration.user.name}</strong>
                                <br />
                                <small className="text-muted">{registration.user.email}</small>
                                {registration.waitlist_position && (
                                  <>
                                    <br />
                                    <small className="text-warning">
                                      Position: {registration.waitlist_position}
                                    </small>
                                  </>
                                )}
                              </div>
                            </td>
                            <td>
                              {getStatusBadge(registration.status)}
                              {!registration.paid && registration.payment_amount > 0 && (
                                <>
                                  <br />
                                  <small className="text-danger">⚠️ Paiement en attente</small>
                                </>
                              )}
                            </td>
                            <td>
                              {registration.payment_amount}€
                              {registration.paid && (
                                <>
                                  <br />
                                  <small className="text-success">✅ Payé</small>
                                </>
                              )}
                            </td>
                            <td>
                              <small>{formatDate(registration.created_at)}</small>
                              {registration.checked_in && (
                                <>
                                  <br />
                                  <small className="text-success">
                                    ✅ Présent le {formatDate(registration.checked_in_at)}
                                  </small>
                                </>
                              )}
                            </td>
                            <td>
                              {registration.status === 'waitlist' && (
                                <button
                                  className="btn btn-sm btn-outline-success me-1"
                                  onClick={() => promoteFromWaitlist(registration.id)}
                                >
                                  ⬆️ Promouvoir
                                </button>
                              )}
                              {registration.status === 'registered' && !registration.checked_in && (
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleCheckIn(registration.id)}
                                >
                                  ✅ Check-in
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {registrations.length === 0 && (
                    <div className="card-body text-center text-muted">
                      Aucune inscription pour le moment
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'checkin' && (
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">Check-in rapide</h6>
                  </div>
                  <div className="card-body">
                    <div className="alert alert-info">
                      <h6>📱 QR Code pour check-in automatique</h6>
                      <p className="mb-2">
                        Les participants peuvent scanner ce QR code pour s'enregistrer automatiquement :
                      </p>
                      <div className="bg-white p-3 rounded d-inline-block">
                        <div className="text-center">
                          📱 QR Code
                          <br />
                          <small>Check-in: {selectedEvent.title}</small>
                        </div>
                      </div>
                      <p className="mt-2 mb-0">
                        <small>
                          URL: {window.location.origin}/events/{selectedEvent.id}/checkin
                        </small>
                      </p>
                    </div>

                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Participant</th>
                            <th>Statut</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {registrations
                            .filter(reg => reg.status === 'registered')
                            .map(registration => (
                            <tr key={registration.id} className={registration.checked_in ? 'table-success' : ''}>
                              <td>
                                <strong>{registration.user.name}</strong>
                                <br />
                                <small className="text-muted">{registration.user.email}</small>
                              </td>
                              <td>
                                {registration.checked_in ? (
                                  <span className="badge bg-success">✅ Présent</span>
                                ) : (
                                  <span className="badge bg-secondary">En attente</span>
                                )}
                              </td>
                              <td>
                                {!registration.checked_in && (
                                  <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => handleCheckIn(registration.id)}
                                  >
                                    ✅ Marquer présent
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'statistics' && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">📊 Statistiques d'inscription</h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <div className="d-flex justify-content-between">
                            <span>Taux de remplissage</span>
                            <strong>
                              {Math.round((stats.registered_count || 0) / selectedEvent.capacity * 100)}%
                            </strong>
                          </div>
                          <div className="progress mt-1">
                            <div
                              className="progress-bar bg-primary"
                              style={{ width: `${(stats.registered_count || 0) / selectedEvent.capacity * 100}%` }}
                            />
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex justify-content-between">
                            <span>Taux de présence</span>
                            <strong>
                              {stats.registered_count > 0 
                                ? Math.round((stats.checked_in_count || 0) / stats.registered_count * 100)
                                : 0}%
                            </strong>
                          </div>
                          <div className="progress mt-1">
                            <div
                              className="progress-bar bg-success"
                              style={{ 
                                width: `${stats.registered_count > 0 
                                  ? (stats.checked_in_count || 0) / stats.registered_count * 100 
                                  : 0}%` 
                              }}
                            />
                          </div>
                        </div>

                        <hr />
                        
                        <div className="row text-center">
                          <div className="col-6">
                            <div className="h5 text-success mb-0">{stats.total_revenue || 0}€</div>
                            <small className="text-muted">Revenus totaux</small>
                          </div>
                          <div className="col-6">
                            <div className="h5 text-info mb-0">
                              {stats.registered_count > 0 
                                ? Math.round((stats.total_revenue || 0) / stats.registered_count * 100) / 100
                                : 0}€
                            </div>
                            <small className="text-muted">Prix moyen</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">⏰ Évolution des inscriptions</h6>
                      </div>
                      <div className="card-body">
                        <div className="text-center text-muted">
                          📈 Graphique d'évolution des inscriptions
                          <br />
                          <small>(Fonctionnalité à développer)</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center p-5">
              <h5>Sélectionnez un événement pour voir ses détails</h5>
              <p className="text-muted">
                Ou <a href="/events/new">créez votre premier événement</a>
              </p>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default OrganizerDashboard;